import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2 as Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  getAdminComplaint,
  markInProgress,
  resolveComplaint,
  type PublicComplaint,
} from '../../lib/complaints-api';
import { uploadOrDataUrl } from '../../lib/files-api';
import { extractApiError } from '../../lib/api-client';
import { formatDate, formatDateTime } from '../../lib/format';
import { useT } from '../../i18n';
import { ComplaintCategoryChip, ComplaintStatusChip } from './chips';
import { unitAddress } from '../../lib/unit-address';

export function ComplaintShowPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const [complaint, setComplaint] = useState<PublicComplaint | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);

  async function refresh() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setComplaint(await getAdminComplaint(id));
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleInProgress() {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await markInProgress(id);
      setFlash(t('comp.markedInProgress'));
      await refresh();
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !complaint) {
    return (
      <Stack spacing={2}>
        <Button startIcon={<ArrowBackIcon />} component={RouterLink} to="/complaints">
          {t('comp.backToList')}
        </Button>
        <Alert severity="error">{error ?? t('comp.notFound')}</Alert>
      </Stack>
    );
  }

  const canMoveInProgress = complaint.status === 'NEW';
  const canResolve = complaint.status === 'NEW' || complaint.status === 'IN_PROGRESS';

  return (
    <Stack spacing={3}>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/complaints"
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('comp.backToList')}
      </Button>

      {flash ? (
        <Alert severity="success" onClose={() => setFlash(null)}>
          {flash}
        </Alert>
      ) : null}

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <div>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h4" fontWeight={600}>
              {complaint.title}
            </Typography>
            <ComplaintStatusChip status={complaint.status} />
            <ComplaintCategoryChip category={complaint.category} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('comp.visibilityLine', {
              vis: t(complaint.visibility === 'PUBLIC' ? 'comp.visPublic' : 'comp.visPrivate'),
              observed: formatDate(complaint.date_observed),
              filed: formatDateTime(complaint.created_at),
            })}
          </Typography>
        </div>
        <Stack direction="row" spacing={1}>
          {canMoveInProgress ? (
            <Button variant="outlined" disabled={busy} onClick={handleInProgress}>
              {t('comp.moveToInProgress')}
            </Button>
          ) : null}
          {canResolve ? (
            <Button variant="contained" color="success" onClick={() => setResolveOpen(true)}>
              {t('comp.markResolved')}
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                {t('comp.detailsSection')}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <DetailRow label={t('comp.location')} value={complaint.location_detail} />
                <DetailRow
                  label={t('comp.reportedBy')}
                  value={
                    complaint.filer_name
                      ? `${complaint.filer_name}${complaint.unit_number ? ` · ${unitAddress(complaint.unit_block, complaint.unit_number)}` : ''}`
                      : `Unit ${complaint.unit_id}`
                  }
                />
                {complaint.in_progress_at ? (
                  <DetailRow
                    label={t('comp.inProgressAt')}
                    value={formatDateTime(complaint.in_progress_at)}
                  />
                ) : null}
                {complaint.resolved_at ? (
                  <DetailRow
                    label={t('comp.resolvedAt')}
                    value={formatDateTime(complaint.resolved_at)}
                  />
                ) : null}
                {complaint.cancelled_at ? (
                  <DetailRow
                    label={t('comp.cancelledAt')}
                    value={formatDateTime(complaint.cancelled_at)}
                  />
                ) : null}
                {complaint.resolved_notes ? (
                  <DetailRow label={t('comp.resolutionNotes')} value={complaint.resolved_notes} />
                ) : null}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                {t('comp.residentPhoto')}
              </Typography>
              <Box sx={{ mt: 1 }}>
                {complaint.photo_url ? (
                  <PhotoView url={complaint.photo_url} alt="Reported issue" />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('comp.noResidentPhoto')}
                  </Typography>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary">
                {t('comp.resolutionPhoto')}
              </Typography>
              <Box sx={{ mt: 1 }}>
                {complaint.resolved_photo_url ? (
                  <PhotoView url={complaint.resolved_photo_url} alt="Resolution evidence" />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('comp.notResolved')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ResolveDialog
        open={resolveOpen}
        onClose={() => setResolveOpen(false)}
        complaintId={complaint.id}
        onResolved={async (msg) => {
          setResolveOpen(false);
          setFlash(msg);
          await refresh();
        }}
      />
    </Stack>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <Grid size={4}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid size={8}>
        <Typography variant="body2">{value}</Typography>
      </Grid>
    </>
  );
}

function PhotoView({ url, alt }: { url: string; alt: string }) {
  // data: URLs render inline. Server URLs need a bearer header, which an <img>
  // tag can't supply — so for an https://system.residen.one/... URL we'd need a
  // server change to allow signed URLs. For MVP we display only data URLs
  // and full https URLs that are publicly accessible; if neither matches,
  // we show the link instead of a broken image.
  const isInline = url.startsWith('data:') || /^https?:\/\//.test(url);
  if (!isInline) {
    return (
      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
        {url}
      </Typography>
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt={alt}
      sx={{
        width: '100%',
        maxHeight: 300,
        objectFit: 'cover',
        borderRadius: 1,
        bgcolor: 'background.default',
      }}
    />
  );
}

// ─── Resolve dialog ────────────────────────────────────────────────────────
function ResolveDialog(props: {
  open: boolean;
  complaintId: string;
  onClose: () => void;
  onResolved: (message: string) => Promise<void>;
}) {
  const t = useT();
  const { open, complaintId, onClose, onResolved } = props;
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [storageWarning, setStorageWarning] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setNotes('');
      setErr(null);
      setStorageWarning(false);
    }
  }, [open]);

  async function handleSubmit() {
    if (!file) {
      setErr(t('comp.noPhoto'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      // uploadOrDataUrl tries POST /files/upload first; on 501 it falls
      // back to a base64 data URL so the demo flow works without R2.
      const url = await uploadOrDataUrl(file);
      if (url.startsWith('data:')) setStorageWarning(true);
      await resolveComplaint(complaintId, {
        resolved_photo_url: url,
        ...(notes.trim() ? { resolved_notes: notes.trim() } : {}),
      });
      await onResolved(t('comp.markedResolved'));
    } catch (e) {
      setErr(extractApiError(e).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('comp.resolveTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {err ? <Alert severity="error">{err}</Alert> : null}
          {storageWarning ? (
            <Alert severity="warning">{t('comp.r2Warning')}</Alert>
          ) : null}
          <Alert severity="info">{t('comp.resolveInfo')}</Alert>
          <Button
            variant="outlined"
            onClick={() => inputRef.current?.click()}
            disabled={submitting}
          >
            {file ? t('comp.selectedPhoto', { name: file.name }) : t('comp.pickPhoto')}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <TextField
            label={t('comp.notesOptional')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            placeholder={t('comp.notesPlaceholder')}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          disabled={submitting}
        >
          {submitting ? t('comp.resolving') : t('comp.markResolved')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
